import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PO_API, poAuth, money, fetchVendors, todayDMY, INPUT, LABEL, BTN_PRIMARY, BTN_GHOST } from './poUtils';
import { FY } from '../../lib/fiscal';

const API = process.env.REACT_APP_BACKEND_URL;
const SHOP = '365 Industrial Solutions Inc.\n263 Prospect St.\nSt. George, ON N0E 1N0\n519-448-4800';
const blankLine = () => ({ description: '', qty: 1, unit_price: '' });

export default function POForm({ po, defaultWo, meName, onClose, onSaved }) {
  const editing = !!po;
  const [form, setForm] = useState(() => ({
    work_order_number: po?.work_order_number || defaultWo || '',
    company_name: po?.company_name || '',
    vendor_contact: po?.vendor_contact || '',
    vendor_email: po?.vendor_email || '',
    vendor_phone: po?.vendor_phone || '',
    ordered_by: po?.ordered_by || meName || '',
    date: po?.date || todayDMY(),
    notes: po?.notes || '',
    ship_mode: po?.ship_to && po.ship_to !== SHOP ? 'custom' : 'shop',
    ship_to: po?.ship_to || '',
    lines: po?.lines?.length ? po.lines.map(l => ({ description: l.description, qty: l.qty ?? 1, unit_price: l.unit_price ?? '' })) : [blankLine()],
    manual_amount: po && !(po.lines?.length) ? String(po.amount ?? '') : '',
  }));
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    axios.get(`${API}/api/projects`, poAuth()).then(r => setProjects((r.data || []).filter(p => /^\d{2}-\d{3,4}$/.test(p.project_number || '')))).catch(() => {});
  }, []);
  useEffect(() => {
    const t = setTimeout(() => fetchVendors(form.company_name).then(setVendors).catch(() => {}), 200);
    return () => clearTimeout(t);
  }, [form.company_name]);

  const useLines = form.lines.some(l => l.description.trim());
  const amount = useMemo(() => useLines
    ? form.lines.reduce((s, l) => s + (Number(l.unit_price) || 0) * (Number(l.qty) || 0), 0)
    : Number(form.manual_amount) || 0, [form.lines, form.manual_amount, useLines]);
  const tax = Math.round(amount * 13) / 100;
  const total = Math.round((amount + tax) * 100) / 100;
  const project = projects.find(p => p.project_number === form.work_order_number.trim().toUpperCase());

  const pickVendor = (v) => setForm(f => ({ ...f, company_name: v.name, vendor_contact: f.vendor_contact || v.contact, vendor_email: f.vendor_email || v.email, vendor_phone: f.vendor_phone || v.phone }));
  const setLine = (i, k, v) => set('lines', form.lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  const save = async () => {
    if (!form.company_name.trim()) return toast.error('Who is the PO to? Add the vendor');
    if (!useLines && !form.manual_amount) return toast.error('Add at least one line (or an amount)');
    setSaving(true);
    try {
      const body = {
        work_order_number: form.work_order_number.trim().toUpperCase(), company_name: form.company_name.trim(),
        vendor_contact: form.vendor_contact, vendor_email: form.vendor_email, vendor_phone: form.vendor_phone,
        ordered_by: form.ordered_by, date: form.date, notes: form.notes,
        ship_to: form.ship_mode === 'custom' ? form.ship_to : (form.ship_mode === 'site' && project?.site_address ? `${project.site_name || project.client_name}\n${project.site_address}` : ''),
        lines: useLines ? form.lines.filter(l => l.description.trim()).map(l => ({ description: l.description, qty: Number(l.qty) || 1, unit_price: l.unit_price === '' ? null : Number(l.unit_price) })) : [],
        amount: useLines ? null : amount, description: useLines ? '' : (form.lines[0]?.description || ''),
      };
      const r = editing
        ? await axios.put(`${PO_API}/${po.po_number}`, body, poAuth())
        : await axios.post(PO_API, body, poAuth());
      toast.success(editing ? `PO ${po.po_number} updated — sheet row rewritten` : `PO ${r.data.po_number} claimed and written to the ${FY.long} Purchase Orders sheet`);
      onSaved(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the PO');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-slate-900/40 backdrop-blur-sm flex items-start justify-center p-3 sm:p-6 overflow-y-auto" data-testid="po-form-modal">
      <div className="ti-panel rounded-2xl w-full max-w-3xl my-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200/80">
          <div>
            <h2 className="text-lg font-black text-slate-900 tracking-tight">{editing ? `Edit PO ${po.po_number}` : 'New Purchase Order'}</h2>
            <p className="text-xs text-slate-500">{editing ? 'Changes are written straight back to the sheet row.' : `The next number is claimed from the ${FY.long} Purchase Orders sheet when you save.`}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-900" data-testid="po-form-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={LABEL}>Work order</label>
            <input list="po-wo-list" value={form.work_order_number} onChange={e => set('work_order_number', e.target.value)} placeholder="26-3092" className={INPUT} data-testid="po-wo-input" />
            <datalist id="po-wo-list">{projects.slice(0, 400).map(p => <option key={p.id} value={p.project_number}>{p.client_name} — {p.title}</option>)}</datalist>
            {project && <p className="text-[11px] text-sky-800 mt-1 truncate">{project.client_name} · {project.title}</p>}
          </div>
          <div>
            <label className={LABEL}>Date</label>
            <input value={form.date} onChange={e => set('date', e.target.value)} placeholder="dd/mm/yyyy" className={INPUT} data-testid="po-date-input" />
          </div>

          <div className="relative sm:col-span-2">
            <label className={LABEL}>Vendor / company</label>
            <input value={form.company_name} onChange={e => set('company_name', e.target.value)} placeholder="Messer, Laser Metal Profiles, Fastenal…" className={INPUT} data-testid="po-vendor-input" autoComplete="off" />
            {vendors.length > 0 && form.company_name && vendors[0].name.toLowerCase() !== form.company_name.trim().toLowerCase() && (
              <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="po-vendor-suggestions">
                {vendors.map(v => (
                  <button key={v.name} type="button" onMouseDown={() => pickVendor(v)} className="w-full text-left px-3 py-2 text-sm hover:bg-sky-50 flex items-center justify-between">
                    <span className="text-slate-900 font-medium">{v.name}</span>
                    <span className="text-[11px] text-slate-500">{v.contact ? `${v.contact} · ` : ''}{v.count} PO{v.count === 1 ? '' : 's'}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div><label className={LABEL}>Contact</label><input value={form.vendor_contact} onChange={e => set('vendor_contact', e.target.value)} className={INPUT} data-testid="po-contact-input" /></div>
          <div><label className={LABEL}>Vendor email <span className="normal-case font-normal text-slate-400">(for emailing the PO)</span></label><input value={form.vendor_email} onChange={e => set('vendor_email', e.target.value)} className={INPUT} data-testid="po-email-input" /></div>

          <div className="sm:col-span-2">
            <div className="flex items-center justify-between mb-1">
              <label className={LABEL + ' mb-0'}>Lines</label>
              <button type="button" onClick={() => set('lines', [...form.lines, blankLine()])} className="text-xs font-semibold text-sky-700 hover:text-sky-900 inline-flex items-center gap-1" data-testid="po-add-line"><Plus className="w-3.5 h-3.5" /> Add line</button>
            </div>
            <div className="space-y-1.5">
              {form.lines.map((l, i) => (
                <div key={i} className="grid grid-cols-[1fr_70px_110px_110px_32px] gap-1.5 items-center" data-testid={`po-line-${i}`}>
                  <input value={l.description} onChange={e => setLine(i, 'description', e.target.value)} placeholder="Description" className={INPUT} data-testid={`po-line-desc-${i}`} />
                  <input value={l.qty} onChange={e => setLine(i, 'qty', e.target.value)} inputMode="decimal" placeholder="Qty" className={INPUT + ' text-right'} data-testid={`po-line-qty-${i}`} />
                  <input value={l.unit_price} onChange={e => setLine(i, 'unit_price', e.target.value)} inputMode="decimal" placeholder="Unit $" className={INPUT + ' text-right'} data-testid={`po-line-unit-${i}`} />
                  <div className="text-right text-sm font-semibold text-slate-800 tabular-nums pr-1">{money((Number(l.unit_price) || 0) * (Number(l.qty) || 0))}</div>
                  <button type="button" onClick={() => set('lines', form.lines.length > 1 ? form.lines.filter((_, j) => j !== i) : [blankLine()])} className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            {!useLines && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs text-slate-500">No priced lines yet — or just enter the amount:</span>
                <input value={form.manual_amount} onChange={e => set('manual_amount', e.target.value)} inputMode="decimal" placeholder="0.00" className={INPUT + ' w-32 text-right'} data-testid="po-manual-amount" />
              </div>
            )}
          </div>

          <div className="sm:col-span-2 grid grid-cols-3 gap-2 rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm">
            <div><p className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Subtotal</p><p className="font-semibold text-slate-900 tabular-nums" data-testid="po-subtotal">{money(amount)}</p></div>
            <div><p className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">HST 13%</p><p className="font-semibold text-slate-900 tabular-nums" data-testid="po-tax">{money(tax)}</p></div>
            <div><p className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Total</p><p className="font-black text-sky-800 tabular-nums" data-testid="po-total">{money(total)}</p></div>
          </div>

          <div>
            <label className={LABEL}>Ordered by</label>
            <input value={form.ordered_by} onChange={e => set('ordered_by', e.target.value)} className={INPUT} data-testid="po-ordered-by" />
          </div>
          <div>
            <label className={LABEL}>Ship to</label>
            <select value={form.ship_mode} onChange={e => set('ship_mode', e.target.value)} className={INPUT} data-testid="po-ship-mode">
              <option value="shop">365 shop — 263 Prospect St, St. George</option>
              <option value="site" disabled={!project?.site_address}>Job site{project?.site_address ? ` — ${project.site_address}` : ' (no site address on the job)'}</option>
              <option value="custom">Other address…</option>
            </select>
            {form.ship_mode === 'custom' && <textarea value={form.ship_to} onChange={e => set('ship_to', e.target.value)} rows={2} className={INPUT + ' mt-1.5'} placeholder="Name\nStreet\nTown, ON" data-testid="po-ship-custom" />}
          </div>
          <div className="sm:col-span-2">
            <label className={LABEL}>Notes for the vendor <span className="normal-case font-normal text-slate-400">(on the PDF only)</span></label>
            <input value={form.notes} onChange={e => set('notes', e.target.value)} className={INPUT} placeholder="Deliver by Friday, call Blair on arrival…" data-testid="po-notes" />
          </div>
        </div>

        <div className="px-5 py-4 border-t border-slate-200/80 flex items-center justify-end gap-2">
          <button onClick={onClose} className={BTN_GHOST} data-testid="po-form-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className={BTN_PRIMARY} data-testid="po-form-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {editing ? 'Save changes' : 'Claim number & save'}
          </button>
        </div>
      </div>
    </div>
  );
}
